import { useI18n } from '@ecom/i18n';
import { Field, Tabs } from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';

import { AlertsPanel } from '../components/AlertsPanel';
import { AuditJournal } from '../components/AuditJournal';
import { AuthEventsPanel } from '../components/AuthEventsPanel';

import { consultationReason } from '@/lib/consultation-reason';

type TabId = 'audit' | 'events' | 'alerts';

/**
 * Security and audit (lot B3), for a superadmin: the audit journal, the sign-in events and the alerts. READ ONLY - nothing on this
 * screen changes anything but the journal itself, which records every look. Like every audited read of this app it carries a reason
 * (the console's default one, or the one typed in the field); nothing here decides what is allowed, the API and the database do.
 */
export function SecurityPage(): ReactElement {
  const { t } = useI18n();
  const [tab, setTab] = useState<TabId>('alerts');
  const [reason, setReason] = useState('');
  const listReason = consultationReason(reason);

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-ink">{t('admin.security.title')}</h1>
        <p className="text-sm text-inkFaint">{t('admin.security.subtitle')}</p>
      </div>

      <Tabs
        label={t('admin.security.tab.label')}
        tabs={[
          { id: 'alerts', label: t('admin.security.tab.alerts') },
          { id: 'audit', label: t('admin.security.tab.audit') },
          { id: 'events', label: t('admin.security.tab.events') },
        ]}
        activeId={tab}
        onChange={(id) => {
          setTab(id as TabId);
        }}
      >
        <div className="flex flex-col gap-5">
          <div className="rounded-xl border border-line bg-surface p-4">
            <Field label={t('admin.common.reasonConsultationLabel')}>
              {(bind) => (
                <input
                  {...bind}
                  type="text"
                  value={reason}
                  onChange={(event) => {
                    setReason(event.target.value);
                  }}
                  maxLength={500}
                  className="ecom-input"
                  placeholder={t('admin.security.reasonPlaceholder')}
                />
              )}
            </Field>
          </div>

          {tab === 'alerts' ? (
            <AlertsPanel reason={listReason} />
          ) : tab === 'audit' ? (
            <AuditJournal reason={listReason} />
          ) : (
            <AuthEventsPanel reason={listReason} />
          )}
        </div>
      </Tabs>
    </div>
  );
}
